import { Album, UrlItem } from '../types';

/**
 * Validates that an album's coverItemId points to an existing, active image.
 * If invalid, returns null to trigger fallback.
 */
export function sanitizeAlbumCover(album: Album): string | null {
  if (!album.coverItemId) return null;
  const coverItem = album.urls.find(u => u.id === album.coverItemId && !u.deletedAt);
  return coverItem ? album.coverItemId : null;
}

/**
 * Merges two sets of albums, resolving conflicts via updatedAt timestamps.
 * Preserves deletedAt tombstones and handles synced coverItemId.
 */
export function mergeAlbums(local: Album[], remote: Album[]): Album[] {
  const mergedMap = new Map<string, Album>();

  for (const l of local) {
    mergedMap.set(l.id, { ...l });
  }

  for (const r of remote) {
    const l = mergedMap.get(r.id);
    if (!l) {
      mergedMap.set(r.id, { ...r });
    } else {
      const useRemoteMeta = (r.updatedAt || 0) > (l.updatedAt || 0);
      
      const mergedAlbum: Album = {
        ...l,
        name: useRemoteMeta ? r.name : l.name,
        coverItemId: useRemoteMeta ? r.coverItemId : l.coverItemId, // Sync cover selection
        updatedAt: Math.max(l.updatedAt || 0, r.updatedAt || 0),
        deletedAt: useRemoteMeta ? r.deletedAt : l.deletedAt,
        deviceId: useRemoteMeta ? (r.deviceId || l.deviceId) : l.deviceId,
        syncVersion: Math.max(l.syncVersion || 0, r.syncVersion || 0),
        urls: mergeUrls(l.urls || [], r.urls || [])
      };

      // Sanitize cover selection after merging URLs to ensure it's still valid
      mergedAlbum.coverItemId = sanitizeAlbumCover(mergedAlbum);
      
      mergedMap.set(r.id, mergedAlbum);
    }
  }

  return Array.from(mergedMap.values());
}

function mergeUrls(local: UrlItem[], remote: UrlItem[]): UrlItem[] {
  const itemMap = new Map<string, UrlItem>();

  const processItem = (item: UrlItem) => {
    const key = item.id || item.url;
    const existing = itemMap.get(key);

    if (!existing) {
      itemMap.set(key, { ...item });
    } else {
      if ((item.updatedAt || 0) > (existing.updatedAt || 0)) {
        itemMap.set(key, { ...item });
      } else if (item.updatedAt === existing.updatedAt && item.deletedAt && !existing.deletedAt) {
        itemMap.set(key, { ...item });
      }
    }
  };

  local.forEach(processItem);
  remote.forEach(processItem);

  return Array.from(itemMap.values());
}